import { gql, useQuery } from '@apollo/client'
import {
  MultiSelector,
  MultiSelectorContent,
  MultiSelectorInput,
  MultiSelectorItem,
  MultiSelectorList,
  MultiSelectorTrigger,
} from '@/components/ui/multi-selector'
import { Badge } from '@/components/ui/badge'

interface ManagerSelectProps {
  field: {
    value: number[]
    onChange: (value: number[]) => void
  }
}

const GET_ACCOUNT_MANAGERS = gql`
  query FindManyCustomer($where: CustomerWhereInput) {
    findManyCustomer(where: $where) {
      id
      name
      role
    }
  }
`

export default function ManagerSelect({ field }: ManagerSelectProps) {
  const { data, loading } = useQuery(GET_ACCOUNT_MANAGERS, {
    variables: {
      where: {
        role: {
          in: ['manager', 'agent'],
        },
      },
    },
  })

  // Convert field.value to the format expected by MultiSelector
  const selectedValues =
    field.value?.map(id => ({
      id: id.toString(),
      name:
        (data?.findManyCustomer?.find((m: any) => m.id === id)?.name ||
          id.toString()) +
        ` (${data?.findManyCustomer?.find((m: any) => m.id === id)?.role})`,
    })) || []

  // Convert managers to the format expected by MultiSelector
  const managers =
    data?.findManyCustomer?.map((manager: any) => ({
      id: manager.id.toString(),
      name: manager.name,
      role: manager.role,
    })) || []

  const handleValuesChange = (values: Array<{ id: string; name: string }>) => {
    // Convert back to number[] for the form field
    field.onChange(values.map(v => parseInt(v.id)))
  }

  if (loading) return <div>Loading...</div>

  return (
    <MultiSelector values={selectedValues} onValuesChange={handleValuesChange}>
      <MultiSelectorTrigger>
        <MultiSelectorInput placeholder="Select managers..." />
      </MultiSelectorTrigger>
      <MultiSelectorContent>
        <MultiSelectorList>
          {managers.map(manager => (
            <MultiSelectorItem key={manager.id} value={manager}>
              <div className="flex items-center justify-between w-full">
                <span>{manager.name}</span>
                <Badge
                  variant={manager.role === 'manager' ? 'green' : 'lightBlue'}
                  className="ml-2"
                >
                  {manager.role}
                </Badge>
              </div>
            </MultiSelectorItem>
          ))}
        </MultiSelectorList>
      </MultiSelectorContent>
    </MultiSelector>
  )
}
